import './CreatorResultsTable.scss';
import { ReactNode, useCallback, useMemo } from 'react';
import { Badge } from '@Components/Badge';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Checkbox } from '@Components/FormCheck';
import { InfoTooltip } from '@Components/InfoTooltip';
import { BPMTable, TypedHeader, useVisibleRowSelection } from '@Components/StickyTable';
import { TableEmptyState, TableLoading } from '@Components/TableEmptyState';
import { TableProgressBar } from '@Components/TableProgressBar';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { CreatorCell } from './CreatorCell';
import { CreatorRow, VIEWER_DETAILS_TOOLTIP, ViewerDetails } from './creator-discovery-constants';
import { formatCompactCount, formatShare } from './creator-discovery-utils';

const SELECT_COLUMN_WIDTH = 32;
const ROW_HEIGHT = 56;
// A metric the creator doesn't share sorts as the lowest value: first ascending, last descending.
const MISSING_METRIC_SORT_VALUE = -1;

const getChannelId = (row: CreatorRow): string => row.channelId;

interface CreatorResultsTableProps {
  rows: CreatorRow[];
  hasSearched: boolean;
  isLoading: boolean;
  // No Google Ads account, so no search can run.
  searchUnavailable?: boolean;
  error?: unknown;
  // Channel ids.
  selected: Set<string>;
  onSelectedChange: (selected: Set<string>) => void;
  // Shown beside the selection count while anything is selected.
  selectionActions?: ReactNode;
}

// Search results with multi-select. Rows stay in Google's relevance order until a column is
// sorted.
export const CreatorResultsTable = ({
  rows,
  hasSearched,
  isLoading,
  searchUnavailable = false,
  error,
  selected,
  onSelectedChange,
  selectionActions
}: CreatorResultsTableProps) => {
  const { toggleSelected, onFilteredDataChange, headersRenderer } = useVisibleRowSelection({
    rows,
    getId: getChannelId,
    selected,
    onSelectedChange,
    selectAllLabel: 'Select all visible creators'
  });

  // Relevance only comes back for topic searches.
  const hasRelevance = rows.some(row => row.relevance !== undefined);

  const headers = useMemo<TypedHeader<CreatorRow>[]>(
    () => [
      {
        name: 'select',
        label: '',
        width: SELECT_COLUMN_WIDTH,
        nonInteractive: true,
        nonFilterable: true,
        renderer: row => (
          <Checkbox
            checked={selected.has(row.channelId)}
            onChange={() => toggleSelected(row.channelId)}
            aria-label={`Select ${row.displayName}`}
          />
        )
      },
      {
        name: 'displayName',
        label: 'Creator',
        flex: 1,
        minFlexWidth: 240,
        // The filter bar also finds creators by handle and by the Local tag.
        optionsGetter: row => [row.displayName, row.handle, ...(row.isLocal ? ['Local'] : [])],
        renderer: row => (
          <CreatorCell
            displayName={row.displayName}
            handle={row.handle}
            channelUrl={row.channelUrl}
            thumbnailUrl={row.thumbnailUrl}
            isLocal={row.isLocal}
          />
        )
      },
      {
        name: 'subscribers',
        label: 'Subscribers',
        width: 112,
        optionsGetter: row => [formatCompactCount(row.subscribers)],
        renderer: row => formatCompactCount(row.subscribers)
      },
      {
        name: 'views',
        label: 'Total views',
        width: 112,
        optionsGetter: row => [formatCompactCount(row.views)],
        renderer: row => formatCompactCount(row.views)
      },
      {
        name: 'videos',
        label: 'Videos',
        width: 88,
        renderer: row => row.videos.toLocaleString('en-US')
      },
      {
        name: 'averageViews',
        label: 'Avg. views',
        width: 112,
        contentGetter: row => row.averageViews ?? MISSING_METRIC_SORT_VALUE,
        optionsGetter: row => [formatCompactCount(row.averageViews)],
        renderer: row => formatCompactCount(row.averageViews)
      },
      {
        name: 'engagementRate',
        label: 'Engagement',
        width: 112,
        contentGetter: row => row.engagementRate ?? MISSING_METRIC_SORT_VALUE,
        optionsGetter: row => [formatShare(row.engagementRate)],
        renderer: row => formatShare(row.engagementRate)
      },
      ...(hasRelevance
        ? [
            {
              name: 'relevance',
              label: 'Relevance',
              width: 144,
              contentGetter: (row: CreatorRow) => row.relevance ?? MISSING_METRIC_SORT_VALUE,
              optionsGetter: (row: CreatorRow) => [formatShare(row.relevance)],
              renderer: (row: CreatorRow) =>
                row.relevance === undefined ? (
                  '—'
                ) : (
                  <TableProgressBar
                    label={formatShare(row.relevance)}
                    maxCompleted={1}
                    value={row.relevance}
                  />
                )
            }
          ]
        : []),
      {
        name: 'badges',
        label: 'Partnerships',
        // Fits all four badges on one line; wrapped, they overflow the row height.
        width: 424,
        contentGetter: row => row.badges.join(', '),
        optionsGetter: row => row.badges,
        renderer: row => <CreatorBadges badges={row.badges} />
      },
      {
        name: 'viewerDetails',
        label: 'Viewer details',
        width: 152,
        renderer: row =>
          row.viewerDetails === ViewerDetails.SHARED ? (
            row.viewerDetails
          ) : (
            <span className='creator-results-no-viewer-details'>{row.viewerDetails}</span>
          )
      }
    ],
    [selected, toggleSelected, hasRelevance]
  );

  const renderHeader = useCallback(
    (cell: { data: unknown; columnIndex: number }) => {
      if (headers[cell.columnIndex]?.name !== 'viewerDetails') return headersRenderer(cell);
      return (
        <div className='viewer-details-header'>
          {headersRenderer(cell)}
          <InfoTooltip size={Size.SM}>{VIEWER_DETAILS_TOOLTIP}</InfoTooltip>
        </div>
      );
    },
    [headers, headersRenderer]
  );

  const renderNoRows = useCallback(() => {
    if (searchUnavailable) {
      return <TableEmptyState message='Creator search needs a connected Google Ads account.' />;
    }
    if (!hasSearched) {
      return <TableEmptyState message='Search above to find creators.' />;
    }
    return (
      <TableLoading
        isLoading={isLoading}
        error={error}
        errorMessage="Couldn't search creators."
        emptyMessage='No creators match this search.'
        rowHeight={ROW_HEIGHT}
      />
    );
  }, [searchUnavailable, hasSearched, isLoading, error]);

  return (
    <div className='creator-results-table'>
      <BPMTable
        className='creator-results-bpm-table'
        headers={headers}
        data={rows}
        rowHeight={ROW_HEIGHT}
        headersRenderer={renderHeader}
        singleColumnSort
        onFilteredDataChange={onFilteredDataChange}
        noRowsRenderer={renderNoRows}
        additionalControls={
          selected.size === 0 ? undefined : (
            <SelectionControls
              count={selected.size}
              actions={selectionActions}
              onClear={() => onSelectedChange(new Set())}
            />
          )
        }
      />
    </div>
  );
};

interface CreatorBadgesProps {
  badges: string[];
}

const CreatorBadges = ({ badges }: CreatorBadgesProps) => {
  if (badges.length === 0) return '—';
  return (
    <div className='creator-badges'>
      {badges.map(badge => (
        <Badge key={badge} size={Size.SM}>
          {badge}
        </Badge>
      ))}
    </div>
  );
};

interface SelectionControlsProps {
  count: number;
  actions?: ReactNode;
  onClear: () => void;
}

const SelectionControls = ({ count, actions, onClear }: SelectionControlsProps) => (
  <div className='creator-selection-controls'>
    <span className='creator-selection-count'>
      {conditionallyPluralizeWithCount(count, 'creator')} selected
    </span>
    <Button appearance={Appearance.EMPTY} design={Design.NEUTRAL} size={Size.SM} onClick={onClear}>
      Clear selection
    </Button>
    {actions}
  </div>
);
